/**
 * Servicio de cálculo de presupuesto óptico
 * Calcula la viabilidad de enlaces de fibra óptica basado en pérdidas
 * @module services/opticalBudgetService
 */

export interface OpticalBudgetInput {
  potenciaTx: number
  sensibilidadRx: number
  margenDiseno: number
  distanciaKm: number
  cantidadEmpalmes: number
  cantidadConectores: number
  ratioSplitter: string | null
  esDesbalanceado?: boolean
  atenuacionDesbalanceado?: number
}

export interface OpticalBudgetResult {
  potenciaTx: number
  sensibilidadRx: number
  margenDiseno: number
  presupuestoDisponible: number
  perdidaFibra: number
  perdidaEmpalmes: number
  perdidaConectores: number
  perdidaSplitter: number
  perdidaTotal: number
  margenRestante: number
  esViable: boolean
  advertencias: string[]
  detalles: {
    coeficienteFibra: number
    perdidaPorEmpalme: number
    perdidaPorConector: number
  }
}

const COEFICIENTE_FIBRA_DB_KM = 0.35
const PERDIDA_EMPALME_FUSION = 0.1
const PERDIDA_CONECTOR = 0.5
const MARGEN_ENVEJECIMIENTO = 1.0

/**
 * Longitud real de un cable: la ruta geométrica (línea entre postes) más las
 * gazas/reservas de cable enrolladas cerca de cada punta. La distancia lineal
 * siempre va a parecer menor que esto -- es justamente lo que hay que
 * contemplar para atenuación por distancia y presupuesto de material.
 */
export function longitudRealCable(cable: {
  longitudTotal?: number | null
  reservaOrigenMetros?: number | null
  reservaDestinoMetros?: number | null
}): number {
  return (cable.longitudTotal || 0) + (cable.reservaOrigenMetros || 0) + (cable.reservaDestinoMetros || 0)
}

const ATENUACION_SPLITTER: Record<string, number> = {
  '1:2': 3.5,
  '1:4': 7.0,
  '1:8': 10.5,
  '1:16': 14.0,
  '1:32': 17.5,
  '1:64': 21.0,
}

const ATENUACION_DESBALANCEADO: Record<string, { cascada: number; local: number }> = {
  '90/10': { cascada: 0.5, local: 10.0 },
  '85/15': { cascada: 0.7, local: 8.2 },
  '80/20': { cascada: 1.0, local: 7.0 },
  '70/30': { cascada: 1.5, local: 5.2 },
  '60/40': { cascada: 2.2, local: 4.0 },
}

export function calcularPresupuestoOptico(input: OpticalBudgetInput): OpticalBudgetResult {
  const {
    potenciaTx,
    sensibilidadRx,
    margenDiseno,
    distanciaKm,
    cantidadEmpalmes,
    cantidadConectores,
    ratioSplitter,
    esDesbalanceado = false,
    atenuacionDesbalanceado = 0,
  } = input

  const presupuestoDisponible = potenciaTx - sensibilidadRx - margenDiseno

  const perdidaFibra = distanciaKm * COEFICIENTE_FIBRA_DB_KM

  const perdidaEmpalmes = cantidadEmpalmes * PERDIDA_EMPALME_FUSION

  const perdidaConectores = cantidadConectores * PERDIDA_CONECTOR

  let perdidaSplitter = 0
  if (ratioSplitter) {
    if (esDesbalanceado && ATENUACION_DESBALANCEADO[ratioSplitter]) {
      perdidaSplitter = atenuacionDesbalanceado || ATENUACION_DESBALANCEADO[ratioSplitter].local
    } else if (ATENUACION_SPLITTER[ratioSplitter]) {
      perdidaSplitter = ATENUACION_SPLITTER[ratioSplitter]
    }
  }

  const perdidaTotal = perdidaFibra + perdidaEmpalmes + perdidaConectores + perdidaSplitter

  const margenRestante = presupuestoDisponible - perdidaTotal

  const esViable = margenRestante > 0

  const advertencias: string[] = []
  
  if (margenRestante < 3) {
    advertencias.push('Margen de seguridad bajo. Considere reducir pérdidas o usar equipos con mayor potencia.')
  }
  
  if (margenRestante < 0) {
    advertencias.push('El enlace NO es viable. La pérdida total excede el presupuesto disponible.')
  }
  
  if (distanciaKm > 20) {
    advertencias.push('Distancia superior a 20km. Verifique especificaciones del equipo.')
  }
  
  if (cantidadEmpalmes > 10) {
    advertencias.push('Número elevado de empalmes. Considere optimizar la ruta.')
  }
  
  if (perdidaSplitter > 14) {
    advertencias.push('Pérdida de splitter alta. Considere usar cascada de splitters menores.')
  }

  return {
    potenciaTx,
    sensibilidadRx,
    margenDiseno,
    presupuestoDisponible,
    perdidaFibra,
    perdidaEmpalmes,
    perdidaConectores,
    perdidaSplitter,
    perdidaTotal,
    margenRestante,
    esViable,
    advertencias,
    detalles: {
      coeficienteFibra: COEFICIENTE_FIBRA_DB_KM,
      perdidaPorEmpalme: PERDIDA_EMPALME_FUSION,
      perdidaPorConector: PERDIDA_CONECTOR,
    },
  }
}

export function obtenerEspecificacionesGPON(): {
  potenciaTxMin: number
  potenciaTxMax: number
  sensibilidadRx: number
  presupuestoClaseB: number
  presupuestoClaseC: number
} {
  return {
    potenciaTxMin: 1.5,
    potenciaTxMax: 5.0,
    sensibilidadRx: -28.0,
    presupuestoClaseB: 25.0,
    presupuestoClaseC: 30.0,
  }
}

export function obtenerEspecificacionesXGSPON(): {
  potenciaTxMin: number
  potenciaTxMax: number
  sensibilidadRx: number
  presupuestoN1: number
  presupuestoN2: number
  presupuestoE1: number
  presupuestoE2: number
} {
  return {
    potenciaTxMin: 2.0,
    potenciaTxMax: 7.0,
    sensibilidadRx: -28.0,
    presupuestoN1: 29.0,
    presupuestoN2: 31.0,
    presupuestoE1: 33.0,
    presupuestoE2: 35.0,
  }
}

export function calcularDistanciaMaxima(
  presupuesto: number,
  cantidadEmpalmes: number,
  cantidadConectores: number,
  ratioSplitter: string | null
): number {
  let perdidasFijas = 0
  
  perdidasFijas += cantidadEmpalmes * PERDIDA_EMPALME_FUSION
  perdidasFijas += cantidadConectores * PERDIDA_CONECTOR
  perdidasFijas += MARGEN_ENVEJECIMIENTO
  
  if (ratioSplitter && ATENUACION_SPLITTER[ratioSplitter]) {
    perdidasFijas += ATENUACION_SPLITTER[ratioSplitter]
  }
  
  const presupuestoParaFibra = presupuesto - perdidasFijas
  
  if (presupuestoParaFibra <= 0) {
    return 0
  }
  
  return presupuestoParaFibra / COEFICIENTE_FIBRA_DB_KM
}

export function calcularPotenciaEsperada(
  potenciaTx: number,
  distanciaKm: number,
  cantidadEmpalmes: number,
  cantidadConectores: number,
  atenuacionSplitter: number
): number {
  const perdidaTotal = 
    (distanciaKm * COEFICIENTE_FIBRA_DB_KM) +
    (cantidadEmpalmes * PERDIDA_EMPALME_FUSION) +
    (cantidadConectores * PERDIDA_CONECTOR) +
    atenuacionSplitter
  
  return potenciaTx - perdidaTotal
}

export const SPLITTER_RATIOS = Object.keys(ATENUACION_SPLITTER)
export const SPLITTER_RATIOS_DESBALANCEADO = Object.keys(ATENUACION_DESBALANCEADO)

// =============================================================================
// CÁLCULO INTEGRADO CON TRAZABILIDAD REAL
// =============================================================================

export interface TracePathSegment {
  type: 'cable' | 'node'
  id: string
  name?: string
  nodeType?: string
  cableType?: string
  longitudM?: number
  fibras?: number
  /** @deprecated usar napSplitterLossDb (soporta varios splitters por NAP) */
  splitterRatio?: string
  /** Pérdida acumulada de todos los splitters del NAP (dB), desde config guardada */
  napSplitterLossDb?: number
  /** Cantidad de splitters contados en napSplitterLossDb */
  napSplitterCount?: number
  numEmpalmes?: number
  numConectores?: number
}

/**
 * Suma la atenuación de todos los splitters de una NAP (config en nodo).
 * Usa `atenuacion` persistida si existe; si no, deriva de ratio balanceado/desbalanceado.
 */
/** Atenuación (dB) de un solo splitter según config guardada (misma lógica que el total histórico). */
export function singleSplitterLossDb(raw: unknown): number {
  if (!raw || typeof raw !== 'object') return 0
  const s = raw as {
    atenuacion?: number
    tipoSplitter?: string
    ratio?: string
    ratioDesbalanceado?: string
  }
  if (typeof s.atenuacion === 'number' && !Number.isNaN(s.atenuacion) && s.atenuacion >= 0) {
    return s.atenuacion
  }
  if (s.tipoSplitter === 'desbalanceado') {
    const r = s.ratioDesbalanceado || s.ratio
    if (r && ATENUACION_DESBALANCEADO[r]) return ATENUACION_DESBALANCEADO[r].local
    return 0
  }
  const r = s.ratio
  if (r && ATENUACION_SPLITTER[r]) return ATENUACION_SPLITTER[r]
  return 0
}

export function computeNapSplitterTotals(splitters: unknown): { lossDb: number; count: number } {
  if (!Array.isArray(splitters) || splitters.length === 0) {
    return { lossDb: 0, count: 0 }
  }
  let lossDb = 0
  let count = 0
  for (const raw of splitters) {
    const one = singleSplitterLossDb(raw)
    if (one > 0) {
      lossDb += one
      count += 1
    }
  }
  return { lossDb, count }
}

export type NapSplitterPathResult = {
  lossDb: number
  count: number
  /** Hay ingress/egress pero la config no enlaza esos cables por splitters */
  unmatched?: boolean
}

export type SplitterCfg = {
  id: string
  tipoSplitter?: string
  puertosDesbalanceados?: Array<{ puerto: number; atenuacion: number }>
  entrada?: {
    tipoEntrada?: string
    cableId?: string
  }
  entradaFibra?: { cableId?: string }
  salidas?: Array<{
    puerto: number
    destino?: {
      tipo?: string
      cableId?: string
      puertoNAP?: number
      splitterDestinoId?: string
    }
  }>
}

/**
 * Pérdida de UNA salida específica del splitter. Para un desbalanceado, la
 * cascada (90%) y el local (10%) tienen atenuaciones muy distintas -- usar
 * siempre `atenuacion` (que solo guarda el valor del lado local) hace que la
 * ruta que sale por cascada arrastre el número equivocado.
 */
export function salidaLossDb(s: SplitterCfg, puerto: number): number {
  if (s.tipoSplitter === 'desbalanceado' && Array.isArray(s.puertosDesbalanceados)) {
    const p = s.puertosDesbalanceados.find((pp) => pp.puerto === puerto)
    if (p && typeof p.atenuacion === 'number' && !Number.isNaN(p.atenuacion)) {
      return p.atenuacion
    }
  }
  return singleSplitterLossDb(s)
}

function splitterRootIngressCableId(s: SplitterCfg): string | null {
  if (s.entrada?.tipoEntrada === 'splitter') return null
  const c = s.entradaFibra?.cableId || s.entrada?.cableId
  return c && c.trim() ? c : null
}

function puertoDropCableId(puertos: unknown, puertoNAP: number): string | null {
  if (!Array.isArray(puertos)) return null
  for (const p of puertos) {
    if (!p || typeof p !== 'object') continue
    const n = (p as { numero?: number }).numero
    if (n !== puertoNAP) continue
    const drop = (p as { cableDrop?: { cableId?: string } }).cableDrop
    const id = drop?.cableId
    return id && id.trim() ? id : null
  }
  return null
}

/**
 * Suma solo los splitters por los que pasa la ruta lógica entre un cable de entrada al NAP
 * y el cable hacia el siguiente salto (hacia el POP en la traza API).
 */
export function computeNapSplitterLossAlongCables(
  splitters: unknown,
  ingressCableId: string,
  egressCableId: string,
  napPuertos?: unknown
): NapSplitterPathResult {
  const empty = { lossDb: 0, count: 0 }
  if (!ingressCableId?.trim() || !egressCableId?.trim()) {
    return empty
  }
  if (!Array.isArray(splitters) || splitters.length === 0) {
    return empty
  }

  const list = splitters as SplitterCfg[]
  const byId = new Map(list.map((s) => [s.id, s]))

  function salidaMatchesEgress(destino: NonNullable<SplitterCfg['salidas']>[0]['destino']): boolean {
    if (!destino) return false
    if (destino.tipo === 'cable' && destino.cableId === egressCableId) return true
    if (destino.tipo === 'puerto_nap' && destino.puertoNAP != null) {
      const drop = puertoDropCableId(napPuertos, destino.puertoNAP)
      return drop === egressCableId
    }
    return false
  }

  /** DFS: máxima pérdida entre caminos válidos (conservador si hay ambigüedad). */
  function dfs(splitterId: string, visited: Set<string>): NapSplitterPathResult | null {
    if (visited.has(splitterId)) return null
    const s = byId.get(splitterId)
    if (!s) return null

    const nextVis = new Set(visited)
    nextVis.add(splitterId)

    let best: NapSplitterPathResult | null = null

    for (const sal of s.salidas || []) {
      const d = sal.destino
      if (!d) continue

      const myLoss = salidaLossDb(s, sal.puerto)

      if (salidaMatchesEgress(d)) {
        const cand = { lossDb: myLoss, count: 1 }
        if (!best || cand.lossDb > best.lossDb) best = cand
        continue
      }

      if (d.tipo === 'splitter' && d.splitterDestinoId) {
        const sub = dfs(d.splitterDestinoId, nextVis)
        if (sub && !sub.unmatched) {
          const cand = { lossDb: myLoss + sub.lossDb, count: 1 + sub.count }
          if (!best || cand.lossDb > best.lossDb) best = cand
        }
      }
    }

    return best
  }

  const roots = list.filter((s) => splitterRootIngressCableId(s) === ingressCableId)
  if (roots.length === 0) {
    return { ...empty, unmatched: true }
  }

  let best: NapSplitterPathResult | null = null
  for (const r of roots) {
    const res = dfs(r.id, new Set())
    if (res && !res.unmatched) {
      if (!best || res.lossDb > best.lossDb) best = res
    }
  }

  if (!best) {
    return { ...empty, unmatched: true }
  }
  return best
}

export interface IntegratedBudgetInput {
  path: TracePathSegment[]
  potenciaTx?: number
  sensibilidadRx?: number
  margenDiseno?: number
}

export interface IntegratedBudgetResult extends OpticalBudgetResult {
  segmentos: Array<{
    nombre: string
    tipo: string
    perdida: number
    potenciaAcumulada: number
  }>
  longitudTotalKm: number
  totalEmpalmes: number
  totalConectores: number
  totalSplitters: number
}

/**
 * Calcula el presupuesto óptico desde una ruta de trazabilidad real
 * Analiza cada segmento (nodo/cable) y acumula las pérdidas
 */
export function calcularDesdeRuta(input: IntegratedBudgetInput): IntegratedBudgetResult {
  const {
    path,
    potenciaTx = 3.0,
    sensibilidadRx = -28.0,
    margenDiseno = 3.0,
  } = input

  const segmentos: IntegratedBudgetResult['segmentos'] = []
  let perdidaAcumulada = 0
  let longitudTotalKm = 0
  let totalEmpalmes = 0
  let totalConectores = 0
  let totalSplitters = 0
  let perdidaFibra = 0
  let perdidaEmpalmes = 0
  let perdidaConectores = 0
  let perdidaSplitter = 0

  // Procesar cada segmento del path
  for (const segment of path) {
    let perdidaSegmento = 0
    let nombreSegmento = segment.name || segment.id.slice(-8)

    if (segment.type === 'cable') {
      // Calcular pérdida del cable
      const distanciaKm = (segment.longitudM || 0) / 1000
      longitudTotalKm += distanciaKm
      const perdidaCable = distanciaKm * COEFICIENTE_FIBRA_DB_KM
      perdidaFibra += perdidaCable
      perdidaSegmento += perdidaCable

      // Asumir 1 conector por cable (en cada extremo se comparte)
      totalConectores += 1
      const perdidaConectorCable = PERDIDA_CONECTOR
      perdidaConectores += perdidaConectorCable
      perdidaSegmento += perdidaConectorCable

      nombreSegmento = `Cable ${segment.cableType || 'DIST'} ${distanciaKm.toFixed(2)}km`

    } else if (segment.type === 'node') {
      // Procesar nodo según su tipo
      const nodeType = segment.nodeType?.toUpperCase() || ''

      if (nodeType === 'CIERRE' || nodeType === 'NAP') {
        // Asumir empalmes en cierres y NAPs
        const emplamesNodo = segment.numEmpalmes ?? 2
        totalEmpalmes += emplamesNodo
        const perdidaEmpalmesNodo = emplamesNodo * PERDIDA_EMPALME_FUSION
        perdidaEmpalmes += perdidaEmpalmesNodo
        perdidaSegmento += perdidaEmpalmesNodo
      }

      if (nodeType === 'NAP') {
        const fromConfig =
          segment.napSplitterLossDb != null && segment.napSplitterLossDb > 0
            ? {
                loss: segment.napSplitterLossDb,
                n: Math.max(1, segment.napSplitterCount ?? 1),
              }
            : segment.splitterRatio
              ? {
                  loss: ATENUACION_SPLITTER[segment.splitterRatio] || 10.5,
                  n: 1,
                }
              : null
        if (fromConfig) {
          totalSplitters += fromConfig.n
          perdidaSplitter += fromConfig.loss
          perdidaSegmento += fromConfig.loss
          nombreSegmento =
            segment.splitterRatio && !segment.napSplitterLossDb
              ? `NAP ${segment.name || ''} (Splitter ${segment.splitterRatio})`
              : `NAP ${segment.name || ''} (${fromConfig.n} splitter${fromConfig.n > 1 ? 's' : ''}, ${fromConfig.loss.toFixed(2)} dB)`
        }
      }
      if (nodeType === 'OLT' || nodeType === 'POP') {
        // Conectores en OLT/POP
        totalConectores += 1
        const perdidaConectorOLT = PERDIDA_CONECTOR
        perdidaConectores += perdidaConectorOLT
        perdidaSegmento += perdidaConectorOLT
        nombreSegmento = `${nodeType} ${segment.name || ''}`
      } else if (nodeType === 'CLIENTE') {
        // Conector en ONT del cliente
        totalConectores += 1
        const perdidaConectorONT = PERDIDA_CONECTOR
        perdidaConectores += perdidaConectorONT
        perdidaSegmento += perdidaConectorONT
        nombreSegmento = `Cliente ${segment.name || ''}`
      }
    }

    perdidaAcumulada += perdidaSegmento
    segmentos.push({
      nombre: nombreSegmento,
      tipo: segment.type,
      perdida: perdidaSegmento,
      potenciaAcumulada: potenciaTx - perdidaAcumulada,
    })
  }

  const presupuestoDisponible = potenciaTx - sensibilidadRx - margenDiseno
  const perdidaTotal = perdidaFibra + perdidaEmpalmes + perdidaConectores + perdidaSplitter
  const margenRestante = presupuestoDisponible - perdidaTotal
  const esViable = margenRestante > 0

  const advertencias: string[] = []
  if (margenRestante < 3 && margenRestante > 0) {
    advertencias.push('Margen de seguridad bajo. Considere reducir pérdidas.')
  }
  if (!esViable) {
    advertencias.push('El enlace NO es viable. La pérdida total excede el presupuesto.')
  }
  if (longitudTotalKm > 20) {
    advertencias.push('Distancia superior a 20km.')
  }
  if (totalSplitters > 2) {
    advertencias.push('Más de 2 splitters en cascada puede causar problemas.')
  }

  return {
    potenciaTx,
    sensibilidadRx,
    margenDiseno,
    presupuestoDisponible,
    perdidaFibra,
    perdidaEmpalmes,
    perdidaConectores,
    perdidaSplitter,
    perdidaTotal,
    margenRestante,
    esViable,
    advertencias,
    detalles: {
      coeficienteFibra: COEFICIENTE_FIBRA_DB_KM,
      perdidaPorEmpalme: PERDIDA_EMPALME_FUSION,
      perdidaPorConector: PERDIDA_CONECTOR,
    },
    segmentos,
    longitudTotalKm,
    totalEmpalmes,
    totalConectores,
    totalSplitters,
  }
}

/**
 * Estimar presupuesto rápido para un path simple (solo cables y nodos IDs)
 */
export function estimarPresupuestoRapido(
  cables: Array<{ longitudTotal?: number; reservaOrigenMetros?: number; reservaDestinoMetros?: number; tipo?: string }>,
  nodes: Array<{ tipo?: string; config?: any }>,
): IntegratedBudgetResult {
  const path: TracePathSegment[] = []

  // Construir path alternando nodos y cables
  nodes.forEach((node, i) => {
    path.push({
      type: 'node',
      id: `node-${i}`,
      nodeType: (node as any).tipo,
      splitterRatio: (node as any).config?.splitters?.[0]?.ratio,
    })

    if (cables[i]) {
      path.push({
        type: 'cable',
        id: `cable-${i}`,
        cableType: cables[i].tipo,
        longitudM: longitudRealCable(cables[i]),
      })
    }
  })

  return calcularDesdeRuta({ path })
}
